import { useMemo } from "react"
import { isMotionReduced, useSettings } from "../game/settings"

/**
 * The air of the world, made visible — and made to drain in step with the loop.
 *
 * The ambient-light engine already stamps the loop's phase on the document root
 * as data-timeofday (dawn → day → dusk → night, read off how far the loop has
 * run toward the Day 512 deadline). The room's light and the celestial dial's
 * sun both read that one signal; this layer reads it too, so the very air shifts
 * with them: gold dust hanging in the light of day, cooling and thinning through
 * dusk, and — as the sun goes out — turning to falling ash streaked with the
 * last few embers of it. The dread the whole game turns on, carried on the air
 * itself, without a word.
 *
 * Every mote's size, path and timing is rolled once and handed to CSS as custom
 * properties; the stylesheet owns colour, direction and glow per phase, so this
 * component never re-renders as the day is spent — the look changes because the
 * root attribute did. Purely decorative and aria-hidden; it collapses to nothing
 * under prefers-reduced-motion (matched here and in the stylesheet), and its
 * fixed layer never takes a pointer event from the UI beneath it.
 */

const MOTE_COUNT = 26

interface Mote {
  left: number
  size: number
  riseDuration: number
  riseDelay: number
  swayDuration: number
  swayDelay: number
  drift: number
  /** A minority of motes read as embers once the sky is dark enough. */
  ember: boolean
}

function rollMotes(): Mote[] {
  return Array.from({ length: MOTE_COUNT }, (_, i) => {
    const size = 1.4 + Math.random() * 2.6
    return {
      left: Math.random() * 100,
      size,
      // Smaller motes ride higher air and cross slower, like real dust.
      riseDuration: 26 - size * 2 + Math.random() * 12,
      // Negative, so the pool is already spread along its path at first paint
      // rather than all launching from the floor at once.
      riseDelay: -Math.random() * 34,
      swayDuration: 5 + Math.random() * 6,
      swayDelay: -Math.random() * 8,
      drift: (6 + Math.random() * 16) * (Math.random() < 0.5 ? -1 : 1),
      ember: i % 4 === 0,
    }
  })
}

export function AmbientMotes() {
  // Rolled once for the life of the mount; the drift is decoration, not state.
  const motes = useMemo(rollMotes, [])
  const [settings] = useSettings()

  // Drained entirely when the player has trimmed or stilled animation, or their
  // system asks for reduced motion — the stylesheet does the same, so nothing is
  // left drifting behind a "no animation" choice.
  if (isMotionReduced(settings)) {
    return null
  }

  return (
    <div className="ambient-motes" aria-hidden="true">
      {motes.map((mote, i) => (
        <span
          key={i}
          className={`mote${mote.ember ? " mote-ember" : ""}`}
          style={
            {
              left: `${mote.left}%`,
              "--mote-size": `${mote.size}px`,
              "--rise-duration": `${mote.riseDuration}s`,
              "--rise-delay": `${mote.riseDelay}s`,
              "--sway-duration": `${mote.swayDuration}s`,
              "--sway-delay": `${mote.swayDelay}s`,
              "--drift": `${mote.drift}px`,
            } as React.CSSProperties
          }
        >
          <span className="mote-body" />
        </span>
      ))}
    </div>
  )
}
